import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatAmount } from '@/contabilidad/utils/formatters';
import type { Moneda } from '@/contabilidad/types';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { CARD_BASE } from '@/contabilidad/constants/ui';

interface KpiCardProps {
  title: string;
  value: number;
  moneda: Moneda;
  icon: LucideIcon;
  iconBg?: string;
  iconColor?: string;
  /** @deprecated mantenido para retrocompatibilidad */
  variant?: 'income' | 'expense' | 'neutral';
  trend?: { value: number; label: string };
  secondary?: { value: number; moneda: Moneda };
  className?: string;
  children?: React.ReactNode;
}

export function KpiCard({
  title, value, moneda, icon: Icon,
  iconBg = 'bg-gray-100', iconColor = 'text-gray-500',
  trend, secondary, className, children,
}: KpiCardProps) {
  return (
    <div className={cn(
      CARD_BASE,
      'flex items-center gap-4 px-5 py-4',
      className,
    )}>
      {/* Icono */}
      <div className={cn('size-9 rounded-lg flex items-center justify-center shrink-0', iconBg)}>
        <Icon className={cn('size-4', iconColor)} />
      </div>

      {/* Cuerpo */}
      <div className="flex-1 min-w-0">
        <span className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
          {title}
        </span>

        {/* Montos en una línea; bajan a la siguiente si no entran */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="flex items-baseline gap-1 shrink-0">
            <span className="text-xs font-semibold text-muted-foreground">{moneda}</span>
            <span className="text-md font-bold text-gray-900 tabular-nums">
              {formatAmount(value, moneda)}
            </span>
          </span>

          {secondary && (
            <>
              <span className="text-gray-300 text-base leading-none shrink-0">|</span>
              <span className="flex items-baseline gap-1 shrink-0">
                <span className="text-xs font-semibold text-muted-foreground">{secondary.moneda}</span>
                <span className="text-md font-bold text-gray-600 tabular-nums">
                  {formatAmount(secondary.value, secondary.moneda)}
                </span>
              </span>
            </>
          )}
        </div>

        {trend && (
          <div className={cn('flex items-center gap-1 mt-1 text-xs font-medium', trend.value >= 0 ? 'text-blue-600' : 'text-slate-600')}>
            {trend.value >= 0 ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
            {trend.value >= 0 ? '+' : ''}{trend.value}%
            <span className="text-muted-foreground font-normal">{trend.label}</span>
          </div>
        )}
      </div>

      {children}
    </div>
  );
}
